import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { useWorkspace } from "../../store/WorkspaceContext";
import { useAuth } from "../../store/AuthContext";
import { bbox, pathD } from "../../engine/geometry";
import { stripFit, setLayout, stretchSet } from "../../engine/pairs";

const PAD = 2;
const GAP = 0.5;

// Shows the eyebrow halves seated on the vehicle's windshield so the shop sees the fit before cutting. L / W can be adjusted
// right here (everyone): the parts stretch on the roll for this session — the Owner can press Save afterwards to keep it.
export const StripFitDialog = () => {
  const { state, dispatch } = useWorkspace();
  const { user } = useAuth();
  const fit = state.dialog?.type === "stripFit" ? state.dialog : null;
  const parts = useMemo(() => (fit ? state.pieces.filter((p) => fit.ids.includes(p.id)) : []), [fit, state.pieces]);
  const ws = useMemo(() => state.pieces.filter((p) => p.pieceType === "windshield" && p.points?.length > 8).sort((a, b) => bbox(b.points).w - bbox(a.points).w)[0], [state.pieces]);
  const gap = GAP + state.spacing;
  const lay = useMemo(() => (parts.length ? setLayout(parts, gap) : null), [parts, gap]);
  const res = useMemo(() => (parts.length ? stripFit(parts, ws, gap) : null), [parts, ws, gap]);
  const close = () => dispatch({ type: "SET", patch: { dialog: null } });
  const vb = res ? `${res.wb.minX - PAD} ${res.wb.minY - PAD} ${res.wb.w + 2 * PAD} ${res.wb.h + 2 * PAD}` : "0 0 10 10";
  const flush = res && res.typical < 0.15;
  const worst = res ? Math.max(res.maxGap, res.maxOverhang) : 0;
  const apply = (axis, val) => {
    const n = Math.round(parseFloat(String(val).replace(/[^0-9.]/g, "")) * 100) / 100;
    const out = stretchSet(state.pieces, fit.ids, axis, n, gap, lay);
    if (typeof out === "string") return toast.error(out);
    if (!out) return;
    dispatch({ type: "SET_PIECES", pieces: out, commit: true });
    toast.success(`${axis === "L" ? "Length" : "Width"} set to ${n.toFixed(2)}"${user?.role === "admin" ? " — press Save to keep it in the library" : " for this roll"}`);
  };
  return (
    <Dialog open={Boolean(fit)} onOpenChange={(o) => !o && close()}>
      <DialogContent className="panel3d max-w-2xl text-[#e8e9ec]" data-testid="strip-fit-dialog">
        <DialogHeader>
          <DialogTitle className="text-[15px]">Eyebrow fit · {fit?.name}</DialogTitle>
          <DialogDescription className="text-[#9aa0a8]">{state.vehicle ? `${state.vehicle.make} ${state.vehicle.model} ${state.vehicle.year_start}–${state.vehicle.year_end}` : ""} — the halves as they sit on the windshield, seen from outside.</DialogDescription>
        </DialogHeader>
        {res ? (
          <svg viewBox={vb} className="h-auto w-full rounded-md border border-[#27324a] bg-[#0c1017]" data-testid="strip-fit-svg">
            <path d={pathD(res.glass)} fill="#1a2231" stroke="#9aa0a8" strokeWidth={0.12} />
            {res.worlds.map((w, i) => <path key={i} d={pathD(w)} fill="rgba(47,128,255,0.55)" stroke="#7fb8ff" strokeWidth={0.1} data-testid={`strip-fit-part-${i}`} />)}
            <line x1={res.rowBox.minX} y1={res.rowBox.maxY + 1} x2={res.rowBox.maxX} y2={res.rowBox.maxY + 1} stroke="#3ecf8e" strokeWidth={0.08} />
            <text x={(res.rowBox.minX + res.rowBox.maxX) / 2} y={res.rowBox.maxY + 2.4} textAnchor="middle" fill="#3ecf8e" fontSize={1.4} fontFamily="monospace">{res.w.toFixed(2)}"</text>
          </svg>
        ) : (
          <div className="rounded-md border border-dashed border-[#27324a] px-4 py-6 text-center text-[13px] text-[#9aa0a8]" data-testid="strip-fit-empty">
            No full windshield template on file for this vehicle yet, so the fit can't be previewed. You can still adjust the size below.
          </div>
        )}
        {lay && (
          <div className="grid grid-cols-3 gap-2 text-[12px]" data-testid="strip-fit-stats">
            <div className="rounded-md border border-[#2f80ff]/40 bg-[#2f80ff]/5 px-3 py-2" data-testid="strip-fit-size">
              <div className="text-[10px] uppercase tracking-wider text-[#6b7079]">Eyebrow row · {parts.length} parts</div>
              <div className="mt-1 flex flex-wrap items-center gap-1.5 font-mono text-[13px]">
                <SizeInput axis="L" value={lay.w} onApply={apply} disabled={state.locked} />
                <span className="text-[#6b7079]">×</span>
                <SizeInput axis="W" value={lay.h} onApply={apply} disabled={state.locked} />
              </div>
              <div className="mt-1 text-[10px] text-[#9aa0a8]">{user?.role === "admin" ? "Type a size, Enter — then Save to keep it" : "Type a size, Enter — adjusts this roll only"}</div>
            </div>
            {res ? (
              <>
                <Stat label="Glass at the top" value={`${res.wb.w.toFixed(1)}" wide`} sub={`ends ${res.ends[0].toFixed(1)}" / ${res.ends[1].toFixed(1)}" in from the pillars`} />
                <Stat label="Top edge fit" value={flush ? "Follows the glass" : res.maxOverhang > res.maxGap ? `${res.typical.toFixed(2)}" past the edge` : `${res.typical.toFixed(2)}" below the edge`} sub={worst > 0.5 ? `up to ${worst.toFixed(1)}" ${res.maxOverhang > res.maxGap ? "past" : "below"} the glass at the corners` : "along the whole strip"} ok={flush} />
              </>
            ) : <Stat label="Glass" value="—" sub="no windshield template yet" />}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const SizeInput = ({ axis, value, onApply, disabled }) => {
  const [val, setVal] = useState(value.toFixed(2));
  useEffect(() => { setVal(value.toFixed(2)); }, [value]);
  const commit = () => { if (parseFloat(val) !== Math.round(value * 100) / 100) onApply(axis, val); };
  return (
    <label className="flex items-center gap-1" title={`${axis === "L" ? "Length across the glass" : "Height of the strip"} in inches`}>
      <span className="text-[10px] text-[#9aa0a8]">{axis}</span>
      <input data-testid={`strip-fit-input-${axis}`} value={val} disabled={disabled} onChange={(e) => setVal(e.target.value)} onBlur={commit}
        onKeyDown={(e) => { e.stopPropagation(); if (e.key === "Enter") commit(); if (e.key === "Escape") setVal(value.toFixed(2)); }}
        className="field h-6 w-16 px-1 py-0 text-right font-mono text-[12px] text-[#e8e9ec]" />
      <span className="text-[10px] text-[#9aa0a8]">in</span>
    </label>
  );
};

const Stat = ({ label, value, sub, ok }) => (
  <div className="rounded-md border border-[#27324a] bg-[#0c1017] px-3 py-2">
    <div className="text-[10px] uppercase tracking-wider text-[#6b7079]">{label}</div>
    <div className={`font-mono text-[13px] ${ok === undefined ? "text-[#e8e9ec]" : ok ? "text-[#3ecf8e]" : "text-[#f5a524]"}`}>{value}</div>
    {sub && <div className="text-[10px] text-[#9aa0a8]">{sub}</div>}
  </div>
);
